import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { User, UserRole } from '../types';
import Button from '../components/Button';
import { US_STATES } from '../data/curriculumStandards';

interface Child {
    id: string;
    name: string;
    email: string;
    grade: number;
    state: string;
    points: number;
    status: string;
    total_quizzes: number;
    avg_score: number;
    last_quiz_date: string | null;
}

const ParentDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);
    const [children, setChildren] = useState<Child[]>([]);
    const [showAddChild, setShowAddChild] = useState(false);
    const [loading, setLoading] = useState(true);

    // Add Child Form State
    const [childForm, setChildForm] = useState({
        name: '',
        email: '',
        password: '',
        grade: 1,
        state: 'TX'
    });
    const [addChildError, setAddChildError] = useState('');

    // Password Reset State
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');

    // Edit Child State
    const [showEditChild, setShowEditChild] = useState(false);
    const [editChildForm, setEditChildForm] = useState({ name: '', email: '' });
    const [editError, setEditError] = useState('');

    useEffect(() => {
        const initDashboard = async () => {
            // Try to get cached user first
            let currentUser = authService.getCurrentUser();

            // If no cached user, try to refresh from server
            if (!currentUser) {
                currentUser = await authService.refreshUser();
            }

            // If still no user or not a parent, redirect to login
            if (!currentUser || (currentUser.role !== UserRole.PARENT && currentUser.role !== UserRole.ADMIN)) {
                navigate('/login');
                return;
            }

            setUser(currentUser);
            await loadChildren();
            setLoading(false);
        };

        initDashboard();
    }, [navigate]);

    const loadChildren = async () => {
        try {
            const response = await fetch('/api/parent/children', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('clevercubby_token')}`
                }
            });
            const data = await response.json();
            setChildren(data.children || []);
        } catch (error) {
            console.error('Failed to load children:', error);
        }
    };

    const handleAddChild = async (e: React.FormEvent) => {
        e.preventDefault();
        setAddChildError('');

        try {
            const response = await fetch('/api/parent/add-child', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('clevercubby_token')}`
                },
                body: JSON.stringify(childForm)
            });

            const data = await response.json();

            if (response.ok) {
                setShowAddChild(false);
                setChildForm({ name: '', email: '', password: '', grade: 1, state: 'TX' });
                await loadChildren();
            } else {
                setAddChildError(data.message || 'Failed to add child');
            }
        } catch (error) {
            setAddChildError('An error occurred');
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError('');

        if (!selectedChildId) return;

        if (newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters');
            return;
        }

        try {
            const response = await fetch(`/api/parent/child/${selectedChildId}/password`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('clevercubby_token')}`
                },
                body: JSON.stringify({ newPassword })
            });

            const data = await response.json();

            if (response.ok) {
                setShowPasswordReset(false);
                setNewPassword('');
                setSelectedChildId(null);
                alert('Password updated successfully!');
            } else {
                setPasswordError(data.message || 'Failed to update password');
            }
        } catch (error) {
            setPasswordError('An error occurred');
        }
    };

    const handleEditChild = async (e: React.FormEvent) => {
        e.preventDefault();
        setEditError('');

        if (!selectedChildId) return;

        try {
            const response = await fetch(`/api/parent/child/${selectedChildId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('clevercubby_token')}`
                },
                body: JSON.stringify(editChildForm)
            });

            const data = await response.json();

            if (response.ok) {
                setShowEditChild(false);
                setEditChildForm({ name: '', email: '' });
                setSelectedChildId(null);
                await loadChildren(); // Reload to show updated info
            } else {
                setEditError(data.message || 'Failed to update child');
            }
        } catch (error) {
            setEditError('An error occurred');
        }
    };

    const handleViewChild = (childId: string) => {
        // Navigate to child's dashboard view
        navigate(`/parent/child/${childId}`);
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                    <p className="text-slate-600">Loading parent dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-8 rounded-3xl shadow-xl mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">👨‍👩‍👧‍👦 Parent Dashboard</h1>
                        <p className="text-purple-100">Welcome back, {user.name}!</p>
                    </div>
                    <Button
                        variant="secondary"
                        onClick={() => setShowAddChild(true)}
                        className="bg-white text-purple-600 hover:bg-purple-50"
                    >
                        <i className="fas fa-plus mr-2"></i> Add Child
                    </Button>
                </div>
            </div>

            {/* Children List */}
            {children.length === 0 ? (
                <div className="bg-white p-12 rounded-3xl shadow-lg border border-slate-100 text-center">
                    <div className="text-6xl mb-4">👶</div>
                    <h3 className="text-2xl font-bold text-slate-700 mb-2">No Children Added Yet</h3>
                    <p className="text-slate-500 mb-6">Add your first child to start tracking their learning progress!</p>
                    <Button onClick={() => setShowAddChild(true)}>
                        <i className="fas fa-plus mr-2"></i> Add Your First Child
                    </Button>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {children.map(child => (
                        <div key={child.id} className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                            <div className="flex items-center mb-4">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-xl mr-3">
                                    {child.name.charAt(0)}
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-800">{child.name}</h3>
                                    <p className="text-sm text-slate-500">Grade {child.grade} • {child.state}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="bg-indigo-50 p-3 rounded-xl">
                                    <p className="text-xs text-indigo-600 font-bold uppercase">Points</p>
                                    <p className="text-2xl font-bold text-indigo-700">{child.points}</p>
                                </div>
                                <div className="bg-green-50 p-3 rounded-xl">
                                    <p className="text-xs text-green-600 font-bold uppercase">Avg Score</p>
                                    <p className="text-2xl font-bold text-green-700">
                                        {child.avg_score ? Math.round(child.avg_score) : 0}%
                                    </p>
                                </div>
                            </div>

                            <div className="text-sm text-slate-600 mb-4">
                                <p><i className="fas fa-clipboard-check mr-2 text-slate-400"></i> {child.total_quizzes} quizzes completed</p>
                                {child.last_quiz_date && (
                                    <p className="mt-1">
                                        <i className="fas fa-clock mr-2 text-slate-400"></i>
                                        Last quiz: {new Date(child.last_quiz_date).toLocaleDateString()}
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleViewChild(child.id)}
                                >
                                    <i className="fas fa-chart-line mr-2"></i> Progress
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setSelectedChildId(child.id);
                                        setEditChildForm({ name: child.name, email: child.email });
                                        setShowEditChild(true);
                                    }}
                                >
                                    <i className="fas fa-edit mr-2"></i> Edit
                                </Button>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => {
                                    setSelectedChildId(child.id);
                                    setShowPasswordReset(true);
                                }}
                            >
                                <i className="fas fa-key mr-2"></i> Reset Password
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Child Modal */}
            {showAddChild && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-indigo-900">Add a Child</h2>
                            <button onClick={() => setShowAddChild(false)} className="text-slate-400 hover:text-slate-600">
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>

                        {addChildError && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm mb-4 border border-red-100">
                                <i className="fas fa-exclamation-circle mr-1"></i> {addChildError}
                            </div>
                        )}

                        <form onSubmit={handleAddChild} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Child's Name</label>
                                <input
                                    type="text"
                                    required
                                    value={childForm.name}
                                    onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                                    placeholder="e.g. Emma"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Email (optional)</label>
                                <input
                                    type="email"
                                    value={childForm.email}
                                    onChange={(e) => setChildForm({ ...childForm, email: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                                    placeholder="child@email.com"
                                />
                                <p className="text-xs text-slate-400 mt-1">Leave blank to use your email</p>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={6}
                                    value={childForm.password}
                                    onChange={(e) => setChildForm({ ...childForm, password: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                                    placeholder="Create a password"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Grade</label>
                                    <select
                                        value={childForm.grade}
                                        onChange={(e) => setChildForm({ ...childForm, grade: Number(e.target.value) })}
                                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none bg-white"
                                    >
                                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => <option key={g} value={g}>{g === 0 ? 'K' : `Grade ${g}`}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">State</label>
                                    <select
                                        value={childForm.state}
                                        onChange={(e) => setChildForm({ ...childForm, state: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none bg-white"
                                    >
                                        {US_STATES.map(state => (
                                            <option key={state.code} value={state.code}>{state.code}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <Button type="submit" className="flex-1">
                                    <i className="fas fa-plus mr-2"></i> Add Child
                                </Button>
                                <Button variant="outline" onClick={() => setShowAddChild(false)} className="flex-1">
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Password Reset Modal */}
            {showPasswordReset && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-indigo-900">Reset Child Password</h2>
                            <button onClick={() => {
                                setShowPasswordReset(false);
                                setNewPassword('');
                                setPasswordError('');
                            }} className="text-slate-400 hover:text-slate-600">
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>

                        {passwordError && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm mb-4 border border-red-100">
                                <i className="fas fa-exclamation-circle mr-1"></i> {passwordError}
                            </div>
                        )}

                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">New Password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={6}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                                    placeholder="Enter new password (min 6 characters)"
                                />
                            </div>

                            <div className="flex gap-3 mt-6">
                                <Button type="submit" className="flex-1">
                                    <i className="fas fa-key mr-2"></i> Update Password
                                </Button>
                                <Button variant="outline" onClick={() => {
                                    setShowPasswordReset(false);
                                    setNewPassword('');
                                    setPasswordError('');
                                }} className="flex-1">
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Child Modal */}
            {showEditChild && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-indigo-900">Edit Child Details</h2>
                            <button onClick={() => {
                                setShowEditChild(false);
                                setEditError('');
                            }} className="text-slate-400 hover:text-slate-600">
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>

                        {editError && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm mb-4 border border-red-100">
                                <i className="fas fa-exclamation-circle mr-1"></i> {editError}
                            </div>
                        )}

                        <form onSubmit={handleEditChild} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    required
                                    value={editChildForm.name}
                                    onChange={(e) => setEditChildForm({ ...editChildForm, name: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                                    placeholder="Child's Name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    required
                                    value={editChildForm.email}
                                    onChange={(e) => setEditChildForm({ ...editChildForm, email: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue outline-none"
                                    placeholder="Child's Email"
                                />
                            </div>

                            <div className="flex gap-3 mt-6">
                                <Button type="submit" className="flex-1">
                                    <i className="fas fa-save mr-2"></i> Save Changes
                                </Button>
                                <Button variant="outline" onClick={() => {
                                    setShowEditChild(false);
                                    setEditError('');
                                }} className="flex-1">
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParentDashboard;
